import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Loader2, PackageOpen } from "lucide-react";
import { toast } from "sonner";
import { API, inr } from "@/context/ShopContext";

const STATUSES = ["received", "confirmed", "shipped", "delivered", "cancelled"];

const detailText = (err) =>
  typeof err?.detail === "string" ? err.detail : "Something went wrong. Please try again.";

export const AdminOrders = () => {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes] = useState({});
  const [ship, setShip] = useState({});
  const [partners, setPartners] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin/settings`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : {}))
      .then((s) => setPartners(s.shipping_partners || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    const r = await fetch(`${API}/admin/orders${q}`, { credentials: "include" });
    const data = await r.json();
    setOrders(data.orders || []);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const update = async (orderNumber, fields) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/orders/${orderNumber}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(fields),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success("Order updated");
      load();
    } catch (err) {
      toast.error(detailText(err));
    } finally {
      setBusy(false);
    }
  };

  if (orders === null) {
    return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-taupe" /></div>;
  }

  return (
    <div className="mt-10" data-testid="admin-orders">
      <div className="flex flex-wrap gap-2">
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            data-testid={`orders-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`text-[11px] uppercase tracking-[0.15em] px-3.5 py-2 border transition-colors ${
              filter === s ? "border-espresso text-espresso bg-white" : "border-beige text-taupe hover:border-taupe"
            }`}
          >
            {s === "all" ? "All Orders" : s}
          </button>
        ))}
      </div>

      {orders.length === 0 ? (
        <div className="py-20 text-center" data-testid="orders-empty">
          <PackageOpen className="w-8 h-8 text-taupe mx-auto" strokeWidth={1} />
          <p className="font-serif text-xl mt-4">No orders {filter !== "all" ? `with status "${filter}"` : "yet"}</p>
          <p className="text-sm text-taupe mt-2">New orders will appear here the moment a customer checks out.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {orders.map((o) => {
            const open = expanded === o.order_number;
            return (
              <div key={o.order_number} className="bg-white border border-beige" data-testid={`order-${o.order_number}`}>
                <button
                  onClick={() => setExpanded(open ? null : o.order_number)}
                  data-testid={`order-toggle-${o.order_number}`}
                  className="w-full flex flex-wrap items-center gap-x-6 gap-y-2 px-6 py-5 text-left"
                >
                  <span className="font-medium text-sm">{o.order_number}</span>
                  <span className="text-xs text-taupe">{new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                  <span className="text-sm">{o.customer?.name}</span>
                  <span className="text-sm font-medium ml-auto">{inr(o.total)}</span>
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${
                    o.status === "delivered" ? "border-espresso/30 text-espresso" :
                    o.status === "cancelled" ? "border-rose/50 text-rose" : "border-taupe/40 text-taupe"
                  }`} data-testid={`order-status-${o.order_number}`}>{o.status}</span>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-taupe">{o.payment_status}</span>
                  <ChevronDown className={`w-4 h-4 text-taupe transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
                </button>

                {open && (
                  <div className="px-6 pb-6 border-t border-beige pt-5 grid grid-cols-1 md:grid-cols-2 gap-8" data-testid={`order-detail-${o.order_number}`}>
                    <div>
                      <p className="overline-label mb-3">Customer & Delivery</p>
                      <div className="text-sm space-y-1.5 text-espresso/85">
                        <p>{o.customer?.name}</p>
                        <p>{o.customer?.email}</p>
                        <p>{o.customer?.phone}</p>
                        <p className="pt-2">{o.address?.address}, {o.address?.city}, {o.address?.state} — {o.address?.pincode}, {o.address?.country}</p>
                      </div>
                      {o.friday_gift ? (
                        <div className="mt-6 border border-espresso/20 bg-beige/40 px-4 py-3">
                          <p className="overline-label mb-1">Friday Gift</p>
                          <p className="text-sm text-espresso/85" data-testid={`admin-friday-gift-${o.order_number}`}>Include a complimentary LAMSAH mini attar with this order (placed on a Friday).</p>
                        </div>
                      ) : null}
                      {o.gift_note ? (
                        <div className="mt-6 border border-rose/30 bg-rose/5 px-4 py-3">
                          <p className="overline-label mb-1">Gift Note</p>
                          <p className="text-sm text-espresso/85 italic" data-testid={`admin-gift-note-${o.order_number}`}>“{o.gift_note}”</p>
                        </div>
                      ) : null}
                      <p className="overline-label mb-3 mt-6">Items</p>
                      <div className="text-sm space-y-2">
                        {(o.items || []).map((it, i) => (
                          <div key={`${it.id || it.name}-${i}`} className="flex justify-between gap-4">
                            <span className="text-espresso/85">
                              {it.name} — {it.shade} × {it.qty}
                              {it.selections?.length ? <span className="block text-xs text-taupe">{it.selections.join(" · ")}</span> : null}
                            </span>
                            <span>{inr(it.price * it.qty)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-xs text-taupe pt-2 border-t border-beige">
                          <span>Shipping</span><span>{o.shipping === 0 ? "Free" : inr(o.shipping)}</span>
                        </div>
                        <div className="flex justify-between font-medium pt-1">
                          <span>Total</span><span>{inr(o.total)}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="overline-label mb-3">Update Status</p>
                      <select
                        data-testid={`order-status-select-${o.order_number}`}
                        value={o.status}
                        onChange={(e) => update(o.order_number, { status: e.target.value })}
                        disabled={busy}
                        className="input-lux"
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <p className="overline-label mb-3 mt-6">Shipping Partner &amp; Tracking</p>
                      <input
                        list="shipping-partners-list"
                        data-testid={`order-ship-partner-${o.order_number}`}
                        className="input-lux"
                        placeholder="e.g. Delhivery, Blue Dart, DTDC"
                        value={ship[o.order_number]?.partner ?? o.shipping_partner ?? ""}
                        onChange={(e) => setShip({ ...ship, [o.order_number]: { ...(ship[o.order_number] || {}), partner: e.target.value } })}
                      />
                      <datalist id="shipping-partners-list">
                        {partners.map((p) => <option key={p} value={p} />)}
                      </datalist>
                      <input
                        data-testid={`order-tracking-${o.order_number}`}
                        className="input-lux mt-2"
                        placeholder="Tracking / AWB number"
                        value={ship[o.order_number]?.tracking ?? o.tracking_number ?? ""}
                        onChange={(e) => setShip({ ...ship, [o.order_number]: { ...(ship[o.order_number] || {}), tracking: e.target.value } })}
                      />
                      <button
                        onClick={() => update(o.order_number, {
                          shipping_partner: ship[o.order_number]?.partner ?? o.shipping_partner ?? "",
                          tracking_number: ship[o.order_number]?.tracking ?? o.tracking_number ?? "",
                        })}
                        disabled={busy}
                        data-testid={`order-ship-save-${o.order_number}`}
                        className="btn-secondary !px-5 !py-2.5 mt-3"
                      >
                        Save Shipping
                      </button>
                      <p className="text-[11px] text-taupe mt-2">Saved courier &amp; tracking are shown to the customer on the Track Order page and in the shipped email.</p>
                      <p className="overline-label mb-3 mt-6">Internal Note (private — only you see this)</p>
                      <textarea
                        rows={3}
                        data-testid={`order-note-${o.order_number}`}
                        className="input-lux resize-none"
                        placeholder="e.g. Customer requested gift wrap"
                        value={notes[o.order_number] ?? o.internal_note ?? ""}
                        onChange={(e) => setNotes({ ...notes, [o.order_number]: e.target.value })}
                      />
                      <button
                        onClick={() => update(o.order_number, { internal_note: notes[o.order_number] ?? o.internal_note ?? "" })}
                        disabled={busy}
                        data-testid={`order-note-save-${o.order_number}`}
                        className="btn-secondary !px-5 !py-2.5 mt-3"
                      >
                        Save Note
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
